import { useState, useEffect, useCallback, useMemo } from 'react';
import { Tabs, Tab, useTheme } from '@mui/material';
import { TabPanel } from '../../Atoms/TabPanel';
import { UpstackDataGrid } from '../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { PageCard } from '../../Compounds/CardWithTitle';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { DEFAULT_INLINE_TAB_SX_STYLES, DEFAULT_MINI_TABLE_SX_STYLES } from '../../../utils/styleHelpers';
import { ApiAdvancedFilter, FilterableField, PaginatedResponse } from '../../../store/GeneralStore';
import { CustomerCombinedAsset, CustomerCombinedAssetCounts } from '../../../store/CustomerCombinedAsset';
import { CustomerRpmAccount } from '../../../store/CustomerRpmAccount';
import {
  getCustomerCombinedAssetCounts,
  listCustomerCombinedAssets
} from '../../../Api/CustomerCombinedAsset/CustomerCombinedAssetApi';
import { listCustomerRpmAccounts } from '../../../Api/CustomerRpmAccount/CustomerRpmAccountApi';
import { CustomerCombinedAssetColumnDefs } from './CustomerCombinedAssetColumnDefs';
import { CustomerRpmAccountColumnDefs } from './CustomerRpmAccountColumnDefs';
import { mergeAndDeduplicateFilters } from '../../Compounds/UpstackDataGrid/helpers';
import { useSnackbar } from '../../../Context/SnackbarContext';

interface CustomerReferencesProps {
  customerId: string;
  supplierId?: string;
}

interface TabDef {
  name: string;
  countKey: keyof CustomerCombinedAssetCounts;
  type: 'asset' | 'rpm';
  filter?: FilterableField[];
  defaultSort: { sort: string; direction: 'asc' | 'desc' };
}

const TABS: TabDef[] = [
  {
    name: 'Commissioning',
    countKey: 'commissioning',
    type: 'asset',
    filter: [{ field: 'commissionStatus', operator: 'isAnyOf', value: ['Commissioning', 'Partially Commissioning'] }],
    defaultSort: { sort: 'actualFirstCommissionRun', direction: 'desc' }
  },
  {
    name: 'BnC',
    countKey: 'bnc',
    type: 'asset',
    filter: [{ field: 'commissionStatus', operator: 'equals', value: 'Pending Commissions' }],
    defaultSort: { sort: 'expectedFirstCommissionRun', direction: 'desc' }
  },
  {
    name: 'Decommissioned',
    countKey: 'decommissioned',
    type: 'asset',
    filter: [{ field: 'commissionStatus', operator: 'equals', value: 'Decommissioned' }],
    defaultSort: { sort: 'actualDecommissionedDate', direction: 'desc' }
  },
  {
    name: 'Non-Commissionable',
    countKey: 'nonCommissionable',
    type: 'asset',
    filter: [
      {
        field: 'isNonCommissionable',
        operator: 'is',
        value: 'true'
      }
    ],
    defaultSort: { sort: 'assetNumber', direction: 'desc' }
  },
  { name: 'All', countKey: 'all', type: 'asset', defaultSort: { sort: 'assetNumber', direction: 'desc' } },
  {
    name: 'Linked RPM Accounts',
    countKey: 'linkedRpmAccounts',
    type: 'rpm',
    filter: [{ field: 'linkStatus', operator: 'equals', value: 'linked' }],
    defaultSort: { sort: 'firstCommissionRun', direction: 'desc' }
  },
  {
    name: 'Unlinked RPM Accounts',
    countKey: 'unlinkedRpmAccounts',
    type: 'rpm',
    filter: [{ field: 'linkStatus', operator: 'equals', value: 'unlinked' }],
    defaultSort: { sort: 'addedDate', direction: 'desc' }
  }
];

export const CustomerReferences = ({ customerId, supplierId }: CustomerReferencesProps) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [counts, setCounts] = useState<CustomerCombinedAssetCounts | null>(null);
  const [countsLoading, setCountsLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [assetData, setAssetData] = useState<PaginatedResponse<CustomerCombinedAsset>>(
    {} as PaginatedResponse<CustomerCombinedAsset>
  );
  const [rpmData, setRpmData] = useState<PaginatedResponse<CustomerRpmAccount>>(
    {} as PaginatedResponse<CustomerRpmAccount>
  );
  const theme = useTheme();
  const { setSnack } = useSnackbar();

  const type = TABS[selectedTab].type;
  const columnDefs = useMemo(() => {
    const cols =
      type === 'asset' ? CustomerCombinedAssetColumnDefs(selectedTab) : CustomerRpmAccountColumnDefs(selectedTab);
    return cols.filter((col) => !col.hideByDefault);
  }, [type, selectedTab]);
  const filterKeys = useMemo(() => columnDefs.filter((c) => c.filterable).map((c) => c.field), [columnDefs, type]);

  const rowsData = useMemo(() => (type === 'asset' ? assetData : rpmData), [type, assetData, rpmData]);

  useEffect(() => {
    if (!customerId) return;
    setCountsLoading(true);
    getCustomerCombinedAssetCounts(customerId, supplierId).then(({ data }) => {
      if (data) setCounts(data);
      setCountsLoading(false);
    });
  }, [customerId, supplierId]);

  const fetchData = useCallback(
    async (filters?: ApiAdvancedFilter, localLoading?: boolean, _forceRefresh?: boolean) => {
      if (!customerId) return;
      if (localLoading) setDataLoading(true);
      const listFunction = type === 'asset' ? listCustomerCombinedAssets : listCustomerRpmAccounts;
      const currentTab = TABS[selectedTab];
      const tabFilters = [...(currentTab?.filter || [])];
      if (supplierId) {
        tabFilters.push({ field: 'supplierId', operator: 'equals', value: supplierId });
      }
      const mergedFilters: ApiAdvancedFilter = {
        ...filters,
        filters: mergeAndDeduplicateFilters(filters?.filters || [], tabFilters, true),
        orders: filters?.orders || [{ sort: currentTab.defaultSort.sort, direction: currentTab.defaultSort.direction }]
      };
      const { data, error } = await listFunction(customerId, mergedFilters);
      if (data)
        type === 'asset'
          ? setAssetData(data as PaginatedResponse<CustomerCombinedAsset>)
          : setRpmData(data as PaginatedResponse<CustomerRpmAccount>);
      if (error) {
        setSnack({ message: `Error fetching: ${error.message}`, type: 'error', open: true });
      }
      if (localLoading) setDataLoading(false);
    },
    [customerId, supplierId, selectedTab, type]
  );

  useEffect(() => {
    if (!customerId) return;
    fetchData(undefined, true);
  }, [selectedTab, customerId, supplierId]);

  if (!customerId) return null;

  if (countsLoading) {
    return (
      <PageCard title="References">
        <ComponentSpinner sx={{ height: '10rem', margin: 'auto' }} />
      </PageCard>
    );
  }

  const sxStyle = {
    ...DEFAULT_MINI_TABLE_SX_STYLES,
    minHeight: 'inherit',
    height: 'inherit',
    maxHeight: '70vh',
    '& .MuiDataGrid-toolbar[data-testid="grid-toolbar"]': {
      'button[aria-label="Select columns"], button[aria-label="Filter"], button[aria-label="Save View"], button[aria-label="Refresh Table"], button[aria-label="Reset Table"]':
        {
          display: 'none'
        }
    }
  };

  return (
    <PageCard title="References">
      <>
        <Tabs
          sx={DEFAULT_INLINE_TAB_SX_STYLES}
          value={selectedTab}
          variant="scrollable"
          scrollButtons="auto"
          onChange={(_e, index) => setSelectedTab(index)}
          slotProps={{
            indicator: {
              sx: { backgroundColor: theme.palette.primary.main, height: 2, borderRadius: 1 }
            }
          }}>
          {TABS.map((tab) => (
            <Tab
              key={tab.name}
              sx={{ textTransform: 'none', fontSize: '.8rem', minWidth: 'auto' }}
              label={`${tab.name} (${counts?.[tab.countKey] ?? 0})`}
            />
          ))}
        </Tabs>
        {TABS.map((tab, index) => (
          <TabPanel
            key={tab.name}
            index={index}
            value={selectedTab}
            boxPaddingTop={0}>
            <UpstackDataGrid
              advancedFiltering
              apiFilter
              sxStyles={sxStyle}
              columns={columnDefs}
              fetchFunction={fetchData}
              filterKeys={filterKeys}
              quickFilterKeys={['multiFieldSearch']}
              gridSettings={{ muiConfig: {}, disabledFields: [] }}
              initialState={{
                pagination: { paginationModel: { page: 0, pageSize: 25 } },
                sorting: { sortModel: [{ field: tab.defaultSort.sort, sort: tab.defaultSort.direction }] },
                pinnedColumns: { left: columnDefs.filter((col) => col.pinned === 'left').map((col) => col.field) }
              }}
              page={`customerReferences_${tab.countKey}` as never}
              rows={rowsData?.data || []}
              rowCount={rowsData?.totalCount}
              showSearch
              title={`Customer References - ${tab.name}`}
              pageSizeOptions={[25, 50, 100]}
              withoutSave
              disableColumnMenu
              localLoading={dataLoading}
            />
          </TabPanel>
        ))}
      </>
    </PageCard>
  );
};
